import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import ErrorBanner from '../../components/ErrorBanner';
import ColumnHeaderMenu from '../../components/common/ColumnHeaderMenu';
import { INSTALLATION_LIBRARY_COLUMNS, installationColumnOptions, filterAndSortInstallationRows, installationQuoteTypeLabel,
  type InstallationLibraryColumn, type InstallationColumnFilters, type InstallationLibrarySort } from './installationLibraryColumns';
import LibraryRecordCard, {
  LibraryRecordCardDetail,
  LibraryRecordCardList,
  LibraryRecordCardSkeleton,
} from '../../components/common/LibraryRecordCard';
import LibraryFilterSheet, { LibraryFilterSheetField } from '../../components/common/LibraryFilterSheet';
import LibraryFilterChips, { type LibraryFilterChip } from '../../components/common/LibraryFilterChips';
import { GuideQuestionButton, GuideTourOverlay, useGuideTour } from '../../components/guides/GuideTour';
import { INSTALLATIONS_LIBRARY_GUIDE } from '../../components/guides/pageGuideContent';
import { formatShortDate } from '../../domain/dates';
import useLibraryPhoneLayout from '../../hooks/useLibraryPhoneLayout';
import { LibraryFilterToggle } from '../../ui/LibraryFilterToggle';
import { LocationPinIcon } from '../../ui/LocationPinIcon';
import LibrarySearchInput from '../../components/common/LibrarySearchInput';
import LibraryCountCard from '../../components/library/LibraryCountCard';
import useCompactToolbar from '../../hooks/useCompactToolbar';
import {
  archiveInstallationJob,
  fetchInstallations,
  installationErrorMessage,
  restoreInstallationJob,
  transitionInstallationJob,
  type InstallationListRow,
  type InstallationStatus,
} from '../../data/installations';
import { useRoles } from '../../hooks/useRoles';
import useSessionStore from '../../state/session';
import InstallationActionsDropdown from './InstallationActionsDropdown';
import InstallationWizardPage from './InstallationWizardPage';
import { fetchCompletionStages } from '../../data/installationCompletion';
import './Installation.css';

const STATUS_LABELS: Record<InstallationStatus, string> = {
  draft: 'Draft',
  approval_pending: 'Approval Pending',
  approved: 'Approved',
  sent: 'Sent',
  confirmed: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  lost: 'Lost',
  cancelled: 'Cancelled',
};

// The hero's stage counts, as on Service Tickets: tones follow the stage pills, and choosing one sets Stage.
const HERO_COUNTS: ReadonlyArray<{ key: InstallationStatus; tone: string }> = [
  { key: 'draft', tone: 'neutral' },
  { key: 'approval_pending', tone: 'warn' },
  { key: 'sent', tone: 'info' },
  { key: 'confirmed', tone: 'ok' },
  { key: 'in_progress', tone: 'info' },
];

// The quote pipeline: quotes not yet confirmed, Draft to Sent.
const PIPELINE_STATUSES: ReadonlyArray<InstallationStatus> = ['draft', 'approval_pending', 'approved', 'sent'];

function summarizeJobs(rows: ReadonlyArray<InstallationListRow>) {
  const counts: Partial<Record<InstallationStatus, number>> = {};
  let pipelineTotal = 0;
  let pipelineCount = 0;
  rows.forEach(row => {
    counts[row.status] = (counts[row.status] ?? 0) + 1;
    if (PIPELINE_STATUSES.includes(row.status)) {
      pipelineTotal += row.total;
      pipelineCount += 1;
    }
  });
  return { counts, pipelineTotal, pipelineCount };
}

const money = (value: number) => value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
type PendingActionKind = 'in_progress' | 'completed' | 'lost' | 'cancelled' | 'archive' | 'restore';
type PendingAction = { job: InstallationListRow; kind: PendingActionKind };

// Phone cards drop a trailing US country; every address in the list is domestic.
const US_COUNTRY_SUFFIX = /,\s*(?:USA|U\.S\.A\.|US|U\.S\.|United States(?: of America)?)\s*$/i;
const withoutUsCountry = (address: string | null) => (address ? address.replace(US_COUNTRY_SUFFIX, '').trim() || null : null);
const formatHours = (hours: number) => `${hours.toFixed(1).replace(/\.0$/, '')} hr${hours === 1 ? '' : 's'}`;

export default function InstallationsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isAdmin } = useRoles();
  const isCreating = location.pathname === '/installations/new';
  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState<'all' | InstallationStatus>('all');
  const [recordSet, setRecordSet] = React.useState<'active' | 'archived'>('active');
  const [columnFilters, setColumnFilters] = React.useState<InstallationColumnFilters>({});
  const [sort, setSort] = React.useState<InstallationLibrarySort>({ column: 'job', direction: 'desc' });
  const setColumnSelection = React.useCallback((column: InstallationLibraryColumn, values: string[]) => {
    setColumnFilters(current => ({ ...current, [column]: values }));
  }, []);
  const [pendingAction, setPendingAction] = React.useState<PendingAction | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const sessionReady = useSessionStore(state => state.sessionReady);
  const pageRef = React.useRef<HTMLDivElement | null>(null);
  const actionsRef = React.useRef<HTMLDivElement | null>(null);
  const toolbarRef = React.useRef<HTMLDivElement | null>(null);
  const resultsRef = React.useRef<HTMLDivElement | null>(null);
  const filterToggleRef = React.useRef<HTMLButtonElement>(null);
  const isPhone = useLibraryPhoneLayout();
  // Filters move into the sheet at the toolbar's phone width (≤720px); cards and the compact
  // header follow the page's phone layout (≤640px).
  const compactToolbar = useCompactToolbar();
  const [filterSheetOpen, setFilterSheetOpen] = React.useState(false);
  React.useEffect(() => {
    if (!compactToolbar) setFilterSheetOpen(false);
  }, [compactToolbar]);
  React.useLayoutEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return;
    // The shared melt surface changes height when it docks or its filters wrap.
    // Keep the column headings below the actual toolbar in each of those states;
    // AppShell supplies the docking line itself (--library-melt-top).
    const measure = () => {
      pageRef.current?.style.setProperty('--installation-library-toolbar-height', `${toolbar.getBoundingClientRect().height}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(toolbar);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isCreating]);
  const query = useQuery({
    queryKey: ['installations', recordSet],
    queryFn: () => fetchInstallations(recordSet === 'archived' ? { archivedOnly: true } : {}),
    enabled: !isCreating,
  });
  const actionMutation = useMutation({
    mutationFn: async ({ action, reason }: { action: PendingAction; reason: string }) => {
      if (action.kind === 'archive') return archiveInstallationJob(action.job.id, reason);
      if (action.kind === 'restore') return restoreInstallationJob(action.job.id);
      return transitionInstallationJob(action.job.id, action.kind, reason);
    },
    onSuccess: async (_result, variables) => {
      setPendingAction(null);
      setActionError(null);
      await queryClient.invalidateQueries({ queryKey: ['installations'] });
      await queryClient.invalidateQueries({ queryKey: ['installation-completion-stages'] });
      if (variables.action.kind === 'completed') navigate(`/installations/${variables.action.job.id}?tab=completion`);
    },
    onError: error => setActionError(installationErrorMessage(error)),
  });
  const completedIds = (query.data ?? []).filter(row => row.status === 'completed').map(row => row.id);
  const completionStages = useQuery({ queryKey: ['installation-completion-stages', completedIds], queryFn: () => fetchCompletionStages(completedIds), enabled: !isCreating && completedIds.length > 0, staleTime: 0 });
  const stageLabel = React.useCallback((row: InstallationListRow) => (
    row.status === 'completed' && completionStages.data
      ? completionStages.data[row.id] ?? 'Completion review'
      : STATUS_LABELS[row.status]
  ), [completionStages.data]);
  const columnOptions = React.useMemo(() => installationColumnOptions(query.data ?? [], stageLabel), [query.data, stageLabel]);
  const searchedRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return (query.data ?? []).filter(row => (status === 'all' || row.status === status)
      && (!term || [
        row.code,
        row.name,
        row.clientName,
        row.clientBranchName,
        row.venueName,
        row.installationLocation,
      ].some(value => value?.toLowerCase().includes(term))));
  }, [query.data, search, status]);
  const rows = React.useMemo(() => filterAndSortInstallationRows(searchedRows, columnFilters, sort, stageLabel), [searchedRows, columnFilters, sort, stageLabel]);
  const activeColumnCount = Object.values(columnFilters).filter(values => values?.length).length;
  const totalJobCount = query.data?.length ?? 0;
  const jobSummary = React.useMemo(() => summarizeJobs(query.data ?? []), [query.data]);
  // Phone: Stage and Show live in the filter sheet and show as chips when not at their default.
  const filterChips = React.useMemo<LibraryFilterChip[]>(() => {
    const chips: LibraryFilterChip[] = [];
    if (status !== 'all') chips.push({ key: 'stage', label: `Stage: ${STATUS_LABELS[status]}`, onRemove: () => setStatus('all') });
    if (recordSet === 'archived') chips.push({ key: 'show', label: 'Show: Deleted jobs', onRemove: () => setRecordSet('active') });
    (Object.keys(INSTALLATION_LIBRARY_COLUMNS) as InstallationLibraryColumn[]).forEach(column => {
      const selected = columnFilters[column] ?? [];
      if (selected.length) chips.push({ key: `column-${column}`, label: `${INSTALLATION_LIBRARY_COLUMNS[column]}: ${selected.length === 1 ? columnOptions[column].find(option => option.value === selected[0])?.label ?? 'No values selected' : `${selected.length} values`}`, onRemove: () => setColumnSelection(column, []) });
    });
    return chips;
  }, [recordSet, status, columnFilters, columnOptions, setColumnSelection]);
  const jobsNoun = `${recordSet === 'archived' ? 'deleted ' : ''}job${rows.length === 1 ? '' : 's'}`;
  const sortLabel = sort.column === 'job' && sort.direction === 'desc' ? 'newest first' : `${INSTALLATION_LIBRARY_COLUMNS[sort.column]} ${sort.direction === 'asc' ? 'ascending' : 'descending'}`;
  const phoneResultsText = query.isLoading ? 'Loading...' : `${rows.length.toLocaleString()} ${jobsNoun} · ${sortLabel}`;
  const showJobsLabel = query.isLoading ? 'Show jobs' : `Show ${rows.length.toLocaleString()} ${jobsNoun}`;
  const guide = useGuideTour({
    steps: INSTALLATIONS_LIBRARY_GUIDE.steps,
    prefPath: INSTALLATIONS_LIBRARY_GUIDE.prefPath,
    storageKey: INSTALLATIONS_LIBRARY_GUIDE.storageKey,
    version: INSTALLATIONS_LIBRARY_GUIDE.version,
    enabled: sessionReady && !isCreating,
    ready: sessionReady && !isCreating && !query.isLoading,
    resolveTarget: React.useCallback((target: string) => (
      target === 'actions'
        ? actionsRef.current
        : target === 'toolbar'
          ? toolbarRef.current
          : target === 'results'
            ? resultsRef.current
            : null
    ), []),
  });

  // Shared by the inline selects and the phone filter sheet.
  const stageOptions = (
    <>
      <option value="all">All stages</option>
      {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
    </>
  );
  const recordSetOptions = (
    <>
      <option value="active">Active jobs</option>
      <option value="archived">Deleted jobs</option>
    </>
  );

  if (isCreating) {
    return <InstallationWizardPage onCancel={() => navigate('/installations')} onCreated={jobId => navigate(`/installations/${jobId}`)} />;
  }

  return (
    <div ref={pageRef} className="settings-shell settings-page library-page library-page--phone events-page installation-page installation-library-page">
      <div className="settings-page__pinned">
        {/* The Service Tickets hero: headline, actions, then stage counts and the quote pipeline (behind Stats on phones). */}
        <header className="settings-hero library-phone-hero">
          <div className="settings-hero__headline library-phone-hero__group">
            <span className="settings-hero__eyebrow">Installations</span>
            <div className="settings-hero__title-row">
              <h1 className="settings-card__title m0">Installation Jobs</h1>
            </div>
            <p className="settings-hero__body">
              {isPhone
                ? 'Quote, approve, sign, and schedule jobs.'
                : 'Quote, approve, sign, and schedule Installation work without creating fulfillment demand.'}
            </p>
          </div>
          <div ref={actionsRef} className="settings-hero__actions">
            <button type="button" className="btn btn-primary" onClick={() => navigate('/installations/new')}>New Installation quote</button>
            <GuideQuestionButton className="guide-question-button--inline" onClick={guide.start} disabled={!guide.canLaunch} title="Open Installations guide" ariaLabel="Open Installations guide" />
          </div>
          <div className="settings-hero__meta" data-stats-toggle-label="Stats">
            <LibraryCountCard
              label="Total"
              value={query.isLoading ? '...' : totalJobCount}
              hint={query.isLoading ? 'Loading...' : `${totalJobCount} installation job${totalJobCount !== 1 ? 's' : ''}`}
              tone="neutral"
              ariaLabel="Show all installation jobs"
              onSelect={() => setStatus('all')}
            />
            {HERO_COUNTS.map(({ key, tone }) => {
              const count = jobSummary.counts[key] ?? 0;
              return (
                <LibraryCountCard
                  key={key}
                  label={STATUS_LABELS[key]}
                  value={query.isLoading ? '...' : count}
                  hint={query.isLoading ? 'Loading...' : `${count} job${count !== 1 ? 's' : ''}`}
                  tone={count > 0 ? tone : 'neutral'}
                  ariaLabel={`Filter by ${STATUS_LABELS[key]}`}
                  onSelect={() => setStatus(key)}
                />
              );
            })}
            {/* Spans four stages, so it is a figure, not a filter. */}
            <div className="settings-meta-card installation-pipeline-card" data-tone="neutral">
              <span className="settings-meta-card__label">Quote pipeline</span>
              <span className="settings-meta-card__value">{query.isLoading ? '...' : money(jobSummary.pipelineTotal)}</span>
              <span className="settings-meta-card__hint">
                {query.isLoading ? 'Loading...' : `${jobSummary.pipelineCount} open quote${jobSummary.pipelineCount !== 1 ? 's' : ''}`}
              </span>
            </div>
          </div>
        </header>
        <div ref={toolbarRef} className="library-toolbar-block">
          <div className="card soft library-toolbar installation-library-toolbar">
            <div className="library-toolbar__search library-toolbar__search--wide installation-library-search">
              <LibrarySearchInput value={search} onChange={setSearch} placeholder={isPhone ? 'Code, job, client or venue' : 'Code, job, client, venue, or location'} label="Search jobs" />
              {compactToolbar ? (
                <LibraryFilterToggle
                  ref={filterToggleRef}
                  expanded={filterSheetOpen}
                  activeCount={filterChips.length}
                  aria-haspopup="dialog"
                  onClick={() => setFilterSheetOpen(true)}
                />
              ) : null}
            </div>
            {compactToolbar ? (
              <LibraryFilterChips chips={filterChips} label="Active job filters" emptyFocusRef={filterToggleRef} />
            ) : (
              <div className="library-toolbar__filters">
                <label className="library-toolbar__field installation-library-stage">
                  <span>Stage</span>
                  <select className="xs" aria-label="Stage" value={status} onChange={event => setStatus(event.target.value as typeof status)}>
                    {stageOptions}
                  </select>
                </label>
                {activeColumnCount ? <button type="button" className="btn xs" onClick={() => setColumnFilters({})}>Clear column filters</button> : null}
                {isAdmin ? (
                  <label className="library-toolbar__field installation-library-record-set">
                    <span>Show</span>
                    <select className="xs" aria-label="Show" value={recordSet} onChange={event => setRecordSet(event.target.value as typeof recordSet)}>
                      {recordSetOptions}
                    </select>
                  </label>
                ) : null}
              </div>
            )}
            {isPhone ? (
              <div className="library-toolbar__meta">
                <div className="lib-count" aria-live="polite">{phoneResultsText}</div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <LibraryFilterSheet
        open={filterSheetOpen && compactToolbar}
        onClose={() => setFilterSheetOpen(false)}
        onReset={() => {
          setStatus('all');
          setRecordSet('active');
          setColumnFilters({});
        }}
        resetDisabled={filterChips.length === 0}
        resultLabel={showJobsLabel}
        returnFocusRef={filterToggleRef}
      >
        <LibraryFilterSheetField label="Stage">
          <select value={status} onChange={event => setStatus(event.target.value as typeof status)}>
            {stageOptions}
          </select>
        </LibraryFilterSheetField>
        {isAdmin ? (
          <LibraryFilterSheetField label="Show">
            <select value={recordSet} onChange={event => setRecordSet(event.target.value as typeof recordSet)}>
              {recordSetOptions}
            </select>
          </LibraryFilterSheetField>
        ) : null}
      </LibraryFilterSheet>

      <main className="settings-content stack gap-md">
        {query.error || actionError ? <ErrorBanner message={actionError ?? installationErrorMessage(query.error, 'Installation jobs could not be loaded.')} /> : null}
        {completionStages.error ? <ErrorBanner message="Completion review stages could not be loaded. Open an installation to retry." /> : null}
        <section className="installation-card installation-library-card">
          <div ref={resultsRef} className="installation-library-results">
            {isPhone ? (
              <>
                {query.isLoading ? (
                  <LibraryRecordCardList label="Loading Installation jobs" busy>
                    {[0, 1, 2].map(index => <LibraryRecordCardSkeleton key={index} />)}
                  </LibraryRecordCardList>
                ) : rows.length === 0 ? (
                  <div className="installation-library-empty">
                    <div className="installation-library-empty__content">
                      <strong>No Installation jobs match these filters</strong>
                      <span className="fg-muted text-sm">Adjust the search or filters, or start a new quote.</span>
                      {recordSet === 'active' ? <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/installations/new')}>New Installation quote</button> : null}
                    </div>
                  </div>
                ) : (
                  <LibraryRecordCardList label="Installation jobs">
                    {rows.map(row => {
                      const client = [row.clientName, row.clientBranchName].filter(Boolean).join(' · ');
                      const place = [row.venueName, withoutUsCountry(row.installationLocation)].filter(Boolean).join(' · ');
                      const updated = formatShortDate(row.updatedAt);
                      return (
                        <LibraryRecordCard
                          key={row.id}
                          href={`/installations/${row.id}`}
                          onOpen={() => navigate(`/installations/${row.id}`)}
                          code={row.code}
                          badges={<span className="installation-status library-record-card__pill" data-status={row.status}>{stageLabel(row)}</span>}
                          title={row.name}
                          details={client || place ? (
                            <>
                              {client ? <LibraryRecordCardDetail>{client}</LibraryRecordCardDetail> : null}
                              {place ? <LibraryRecordCardDetail icon={<LocationPinIcon />} muted>{place}</LibraryRecordCardDetail> : null}
                            </>
                          ) : undefined}
                          meta={[
                            row.total > 0 ? <span key="quote" className="library-record-card__emphasis">{money(row.total)}</span> : 'No quote yet',
                            row.billedHours > 0 ? formatHours(row.billedHours) : 'No hours',
                            updated ? `Updated ${updated}` : null,
                          ]}
                          actions={(
                            <InstallationActionsDropdown
                              job={row}
                              isAdmin={isAdmin}
                              busy={actionMutation.isPending}
                              onTransition={kind => setPendingAction({ job: row, kind })}
                              onArchive={() => setPendingAction({ job: row, kind: 'archive' })}
                              onRestore={() => setPendingAction({ job: row, kind: 'restore' })}
                            />
                          )}
                        />
                      );
                    })}
                  </LibraryRecordCardList>
                )}
              </>
            ) : (
            <table className="data-table installation-library" aria-label="Installation jobs">
              <thead>
                <tr>
                  {(Object.keys(INSTALLATION_LIBRARY_COLUMNS) as InstallationLibraryColumn[]).map(column => {
                    const numeric = column === 'quote' || column === 'hours';
                    return <th key={column} className={numeric ? 'numeric' : undefined} aria-sort={sort.column === column ? sort.direction === 'asc' ? 'ascending' : 'descending' : 'none'}>
                      <ColumnHeaderMenu label={INSTALLATION_LIBRARY_COLUMNS[column]} variant="master-inventory" emptyMeansAll
                        align={numeric ? 'right' : 'left'} options={columnOptions[column]} selected={columnFilters[column] ?? []}
                        sortDirection={sort.column === column ? sort.direction : null} onSort={direction => setSort({ column, direction })}
                        onChangeSelected={values => setColumnSelection(column, values)} />
                    </th>;
                  })}
                  <th className="installation-library-actions-column">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr
                    key={row.id}
                    tabIndex={0}
                    onClick={() => navigate(`/installations/${row.id}`)}
                    onKeyDown={event => { if (event.key === 'Enter') navigate(`/installations/${row.id}`); }}
                  >
                    <td data-label="Job"><strong>{row.code}</strong><br /><span className="fg-muted text-xs">{row.name}</span></td>
                    <td data-label="Type">{installationQuoteTypeLabel(row)}</td>
                    <td data-label="Client">{row.clientName ?? '—'}{row.clientBranchName ? <small>{row.clientBranchName}</small> : null}</td>
                    <td data-label="Installation location">
                      {row.venueName ? <><strong>{row.venueName}</strong><br /></> : null}
                      {row.installationLocation ?? '—'}
                    </td>
                    <td data-label="Stage"><span className="installation-status" data-status={row.status}>{stageLabel(row)}</span></td>
                    <td data-label="Quote" className="numeric">{money(row.total)}</td>
                    <td data-label="Billed hours" className="numeric">{row.billedHours.toFixed(1)}</td>
                    <td data-label="Updated">{new Date(row.updatedAt).toLocaleDateString()}</td>
                    <td data-label="Actions" className="installation-library-actions-column" onClick={event => event.stopPropagation()}>
                      <InstallationActionsDropdown
                        job={row}
                        isAdmin={isAdmin}
                        busy={actionMutation.isPending}
                        onTransition={kind => setPendingAction({ job: row, kind })}
                        onArchive={() => setPendingAction({ job: row, kind: 'archive' })}
                        onRestore={() => setPendingAction({ job: row, kind: 'restore' })}
                      />
                    </td>
                  </tr>
                ))}
                {!query.isLoading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="installation-library-empty">
                      <div className="installation-library-empty__content">
                        <strong>No Installation jobs match these filters</strong>
                        <span className="fg-muted text-sm">Adjust the search or stage, or start a new quote.</span>
                        {recordSet === 'active' ? <button type="button" className="btn btn-primary btn-sm" onClick={event => { event.stopPropagation(); navigate('/installations/new'); }}>New Installation quote</button> : null}
                      </div>
                    </td>
                  </tr>
                ) : null}
                {query.isLoading ? <tr><td colSpan={9} className="installation-library-empty">Loading Installation jobs…</td></tr> : null}
              </tbody>
            </table>
            )}
          </div>
        </section>
      </main>
      {pendingAction ? (
        <InstallationActionDialog
          action={pendingAction}
          busy={actionMutation.isPending}
          error={actionError}
          onCancel={() => {
            if (!actionMutation.isPending) {
              setPendingAction(null);
              setActionError(null);
            }
          }}
          onConfirm={reason => actionMutation.mutate({ action: pendingAction, reason })}
        />
      ) : null}
      {!pendingAction && guide.open && guide.activeStep ? <GuideTourOverlay activeStep={guide.activeStep} ariaLabel="Installations library guided tour" isFinalStep={guide.isFinalStep} onClose={guide.close} onNext={guide.next} onPrev={guide.prev} spotlightRect={guide.spotlightRect} stepCountLabel={guide.stepCountLabel} stepIndex={guide.stepIndex} /> : null}
    </div>
  );
}

function InstallationActionDialog({ action, busy, error, onCancel, onConfirm }: {
  action: PendingAction;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = React.useState('');
  const needsReason = ['lost', 'cancelled', 'archive'].includes(action.kind);
  const labels: Record<PendingActionKind, string> = {
    in_progress: 'Mark In Progress',
    completed: 'Mark Complete',
    lost: 'Mark Lost',
    cancelled: 'Cancel job',
    archive: 'Delete job',
    restore: 'Restore job',
  };
  return (
    <div className="modal-overlay" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onCancel(); }}>
      <section className="modal installation-action-dialog" role="dialog" aria-modal="true" aria-labelledby="installation-action-title">
        <header className="modal-header">
          <div><span className="installation-section-kicker">{action.job.code}</span><h2 id="installation-action-title" className="m0">{labels[action.kind]}</h2></div>
          <button type="button" className="btn-close" aria-label="Close" onClick={onCancel} disabled={busy}>×</button>
        </header>
        <div className="modal-body stack gap-sm">
          <p className="m0">{action.kind === 'archive' ? 'This removes the job from normal views while preserving its audit and schedule history.' : `Apply this action to ${action.job.name}?`}</p>
          {needsReason ? <label className="installation-field"><span>Reason *</span><textarea className="input" value={reason} onChange={event => setReason(event.target.value)} autoFocus placeholder="Record the reason for this action" /></label> : null}
          {error ? <ErrorBanner message={error} /> : null}
        </div>
        <footer className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className={`btn ${action.kind === 'archive' ? 'btn-danger' : 'btn-primary'}`} onClick={() => onConfirm(reason.trim())} disabled={busy || (needsReason && !reason.trim())}>{busy ? 'Saving…' : labels[action.kind]}</button>
        </footer>
      </section>
    </div>
  );
}
